import { Config } from "../Config";
import logger from "./Logger"

export const EVENT_USER_STT_RESULT = "DEAIService#EVENT_USER_STT_RESULT";
export const EVENT_AI_TEXT_RESPONSE = "DEAIService#EVENT_AI_TEXT_RESPONSE";
export const EVENT_AI_AUDIO_ENQUEUED = "DEAIService#EVENT_AI_AUDIO_ENQUEUED";

class DEAIServiceEvent extends Event{
    constructor(type, message) {
        super(type)
        this.message = message;
    }
}

export default class DEAIService extends EventTarget {
    async initialize(wsUrl) {
        if(this.socket) {
            return;
        }

        this.messageIndex = 0;
        this.latency = 0;
        this.latencyIntervalId = null;
        this.isProcessing = false;
        this.isAIResponding = false;
        this.inputTTSMessages = [];
        this.currentAIResponse = "";
        this.firstResponseTime = -1;
        this.latencyMetrics = {};
        this.messagesState = [];
        this.audioQueue = [];
        this.sessionId = await this.getSessionId(wsUrl);
        return new Promise((resolve, reject) => {
            let currentUrl = window.location;
            let wsProtocol = currentUrl.protocol === 'https:' ? 'wss:' : 'ws:';
            wsUrl = wsUrl ?? `${wsProtocol}//${currentUrl.host}`;
            this.socket = new WebSocket(`${wsProtocol}//${wsUrl}/ws/${this.sessionId}`);
    
            this.socket.onopen = () => {
                logger.log('WebSocket connected');
                this.statusText = 'Ready';
                // recordButton.disabled = false;
                // this.startLatencyMeasurement();
                resolve(this);
            };
    
            this.socket.onclose = (event) => {
                logger.log(`WebSocket disconnected. Code: ${event.code}, Reason: ${event.reason}`);
                this.statusText = 'Disconnected';
                // recordButton.disabled = true;
                // this.stopLatencyMeasurement();
                reject(new Error('WebSocket closed'));
            };
    
            this.socket.onerror = (error) => {
                logger.log(`WebSocket error: ${error.message}`);
                this.statusText = 'Error';
                reject(error);
            };
    
            this.socket.onmessage = async (event) => {
                // logger.log(`Received message from server: ${typeof event.data}`);
                if (event.data instanceof Blob) {
                    // Handle audio data
                    this.queueAudioForPlayback(event.data);
                } else {
                    // Handle JSON messages
                    try {
                        const message = JSON.parse(event.data);

                        // Don't spam logs...
                        if (message.type !== 'pong') {
                            logger.log(`Parsed server message: ${JSON.stringify(message)}`);
                        }

                        if (message.type === 'pong') {
                            this.latency = performance.now() - this.ping;
                            this.updateLatencyDisplay(this.latency);
                        } else if (message.type === 'first_audio_response') {
                            this.firstResponseTime = Date.now();
                        } else if (message.type === 'latency_metrics') {
                            this.updateLatencyMetrics(message.metrics);
                        } else if (message.type === 'processing_complete') {
                            console.log(this.currentAIResponse);
                            this.isProcessing = false;
                            this.startLatencyMeasurement();
                            this.isAIResponding = false;
                        } else if (message.type === 'error') {
                            logger.log(`Error from server: ${message.message}`);
                        }
                        
                        if(message.action === 'message') {
                            if (message.role === 'assistant') {
                                this.currentAIResponse += message.content;
                                this.dispatchEvent(new DEAIServiceEvent(EVENT_AI_TEXT_RESPONSE, this.currentAIResponse));
                                this.messagesState[this.messageIndex - 1].text = this.currentAIResponse;
                            } else if (message.role === 'user') {
                                this.dispatchEvent(new DEAIServiceEvent(EVENT_USER_STT_RESULT, message.content));
                                this.currentAIResponse = "";
                                this.messagesState.push({
                                    id: this.messageIndex++, 
                                    text: message.content, 
                                    isSender: true
                                });
                                this.messagesState.push({
                                    id: this.messageIndex++, 
                                    text:  this.currentAIResponse, 
                                    isSender: false
                                });
                            } 
                        }
                       
                    } catch (error) {
                        logger.log(`Error parsing server message: ${error.message}`);
                    }
                }
            };
        });
    }

    async getSessionId(wsUrl) {
        const sessionId = window.localStorage.getItem("sid");
        if (sessionId) {
          console.log(`Session ID found ${sessionId}`);
          return sessionId;
        }
        try {
          const response = await fetch(`${Config.HTTPS?'https':'http'}://${wsUrl}/session`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Access-Control-Allow-Origin': '*'
            },
            body: JSON.stringify({ action: 'new' })
          });
  
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
  
          const data = await response.json();
          const newSessionId = data.sessionId;
          if (newSessionId) {
            window.localStorage.setItem("sid", newSessionId);
            return newSessionId;
          } else {
            return null;
          }
        } catch (error) {
          // showQosMessage('⚠ Error Creating Session. Please try again later!', -1)
          console.error('Error fetching session ID:', error);
          return null;
        }
      }

    queueAudioForPlayback(audio) {
        this.audioQueue.push(audio);
        this.dispatchEvent(new DEAIServiceEvent(EVENT_AI_AUDIO_ENQUEUED, this.audioQueue));
    }

    updateLatencyDisplay(latency) {
        this.latency = latency;
    }

    updateLatencyMetrics(metrics) {
        Object.assign(this.latencyMetrics, metrics);
    }

    startLatencyMeasurement() {
        if (this.latencyIntervalId === null) {
            this.latencyIntervalId = setInterval(()=>this.measureLatency(), 1000);
        }
    }

    stopLatencyMeasurement() {
        if (this.latencyIntervalId !== null) {
            clearInterval(this.latencyIntervalId);
            this.latencyIntervalId = null;
        }
    }

    measureLatency() {
        if (!this.isProcessing) {
            this.ping = performance.now();
            this.socket.send(JSON.stringify({ type: 'ping' }));
        }
    }

    submitVoice(blob) {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            logger.log(`Sending audio file: ${blob.size} bytes`);
            this.socket.send(blob);
            const stopMessage = JSON.stringify({ action: "stop_recording" });
            logger.log(`Sending stop_recording message: ${stopMessage}`);
            this.socket.send(stopMessage);
        } else {
            logger.log('WebSocket is not open. Cannot send audio.');
        }

    }

}

let service;

export function useDEAIService(wsUrl) {
    if(!service) {
        service = new DEAIService();
        service.initialize(wsUrl);
    }
    return service;
}